import React, { useState, useEffect, useRef } from 'react';
import { useGetIdentity, useDataProvider, useRecordContext } from 'react-admin';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { MessageSquare, Mail, StickyNote, Send, Loader2, Wand2, AlertTriangle } from 'lucide-react';
import { AddNoteDialog } from './AddNoteDialog';
import { createNote } from './notesApi';
import { translateText } from './translateApi';
import { getToken } from '../../authentication/authProvider';
import { CustomerEvent, CustomerEventType, Funeral } from '../../types';
import { useMessageDraft } from '../contexts/MessageDraftContext';
import { EmailComposeDialog, CaseEmailRecipient } from '../../components/EmailComposeDialog';
import { CasePartnerChip, PartnerRecipientChip } from './PartnerRecipientChip';
import { useAutoGrowTextarea } from '../../hooks/useAutoGrowTextarea';

type Channel = 'sms' | 'email' | 'note';

// The message box grows with its text: two lines when empty, up to six (but
// never more than a third of the screen), then it scrolls inside. text-sm is a
// 1.25rem line; py-2 adds 1rem and the border 2px.
const TEXTAREA_MIN_HEIGHT = 'calc(2 * 1.25rem + 1rem + 2px)';
const TEXTAREA_MAX_HEIGHT = 'min(calc(6 * 1.25rem + 1rem + 2px), 35vh)';

const channels: { id: Channel; label: string; icon: React.ElementType; placeholder: string }[] = [
  { id: 'sms', label: 'SMS', icon: MessageSquare, placeholder: 'Type an SMS message...' },
  { id: 'email', label: 'Email', icon: Mail, placeholder: 'Type an email...' },
  { id: 'note', label: 'Note', icon: StickyNote, placeholder: 'Add an internal note...' },
];

const API_URL = process.env.REACT_APP_PAPERMAN_API_URL || 'http://localhost:3001';
const baseUrl = API_URL.endsWith('/') ? API_URL.slice(0, -1) : API_URL;

interface MessageInputBarProps {
  clients?: { id: number; name: string; color: string; phone_number: string; email?: string | null }[];
  /** Partners with a role on the case (case feed only). Shown after the clients;
   *  selecting one opens an email to that partner alone. */
  partners?: CasePartnerChip[];
  /** False hides the partner chips (and their '|') while keeping the rest of the
   *  recipient row as-is — the inbox shows only the family. Defaults to true. */
  showPartners?: boolean;
  defaultRecipientId?: number | null;
  /** Client who sent the most recent inbound SMS. When the user sends an SMS to
   *  a different client, a confirmation warning is shown. Null when unknown. */
  lastInboundClientId?: number | null;
  onMessageSent?: (event: CustomerEvent) => void;
  /** When provided, notes are saved with this lead_id instead of funeral_id */
  leadId?: string;
  /** When provided, notes are saved with this funeral_id. Falls back to record context. */
  funeralId?: number;
}

export function MessageInputBar({ clients = [], partners = [], showPartners = true, defaultRecipientId, lastInboundClientId, onMessageSent, leadId, funeralId }: MessageInputBarProps) {
  const [activeChannel, setActiveChannel] = useState<Channel>('sms');
  const [selectedRecipientId, setSelectedRecipientId] = useState<number | null>(null);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [confirmRecipient, setConfirmRecipient] = useState(false);
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const { identity } = useGetIdentity();
  const dataProvider = useDataProvider();
  const record = useRecordContext<Funeral>();
  const active = channels.find((c) => c.id === activeChannel)!;
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  useAutoGrowTextarea(textareaRef, message);

  // Resolve funeral ID from explicit prop or record context
  const resolvedFuneralId = funeralId ?? (record?.id ? Number(record.id) : undefined);
  // The case an email is about. Never on the lead page, whose record context is
  // the lead (its uuid is not a funeral id).
  const caseFuneralId =
    !leadId && resolvedFuneralId != null && Number.isFinite(resolvedFuneralId)
      ? resolvedFuneralId
      : undefined;

  // Email opens the contact card's dialog rather than a textarea mode. A
  // selected partner chip only lasts while its email dialog is open, so the
  // SMS recipient underneath is never a partner.
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailPartnerId, setEmailPartnerId] = useState<string | null>(null);
  const [emailHint, setEmailHint] = useState<string | null>(null);

  // Pick up draft messages from ProcessTimeline's "Request Missing Info"
  const { draftMessage, clearDraft, focusRequest } = useMessageDraft();
  useEffect(() => {
    if (draftMessage) {
      setMessage(draftMessage);
      setActiveChannel('sms');
      clearDraft();
    }
  }, [draftMessage, clearDraft]);

  // Focus requests from the contact-card SMS quick action: switch channel,
  // select the matching recipient (by phone), and focus the textarea.
  useEffect(() => {
    if (!focusRequest) return;
    if (focusRequest.channel) setActiveChannel(focusRequest.channel);
    if (focusRequest.recipientPhone) {
      const digits = focusRequest.recipientPhone.replace(/\D/g, '');
      const match = clients.find(
        (c) => c.phone_number && c.phone_number.replace(/\D/g, '').endsWith(digits.slice(-10)),
      );
      if (match) setSelectedRecipientId(match.id);
    }
    // Defer focus to after the channel/recipient state settles.
    const t = setTimeout(() => textareaRef.current?.focus(), 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusRequest?.nonce]);

  const showRecipientPicker =
    (clients.length >= 2 || partners.length > 0) && activeChannel !== 'note';

  // Set default recipient when it becomes available
  useEffect(() => {
    if (defaultRecipientId != null && selectedRecipientId === null) {
      setSelectedRecipientId(defaultRecipientId);
    }
  }, [defaultRecipientId, selectedRecipientId]);

  const selectedClient = clients.find((c) => c.id === selectedRecipientId);
  // For single-client funerals, use the only client
  const recipientClient = selectedClient || (clients.length === 1 ? clients[0] : null);

  // Warn when about to text a different contact than whoever last messaged us.
  const lastInboundClient =
    lastInboundClientId != null ? clients.find((c) => c.id === lastInboundClientId) : undefined;
  const recipientMismatch =
    activeChannel === 'sms' &&
    !!lastInboundClient &&
    !!recipientClient &&
    lastInboundClient.id !== recipientClient.id;

  const hasEntityId = !!leadId || !!resolvedFuneralId;
  const canSendSms = activeChannel === 'sms' && message.trim() && recipientClient && !sending;
  const canSendNote = activeChannel === 'note' && message.trim() && !sending && hasEntityId;
  const canSend = canSendSms || canSendNote;

  const handleTranslateToFrench = async () => {
    if (!message.trim()) return;
    setTranslating(true);
    try {
      const translated = await translateText(message.trim(), 'fr');
      setMessage(translated);
    } catch (err: any) {
      console.error('Translation failed:', err);
      alert(`Translation failed: ${err?.message || 'Unknown error'}`);
    } finally {
      setTranslating(false);
    }
  };

  const handleSendSms = async () => {
    if (!recipientClient) return;
    const sentText = message.trim();

    const token = await getToken();
    const body: Record<string, any> = {
      message: sentText,
      senderName: identity?.fullName || undefined,
    };
    // Always send phone_number when available (works for both leads and funerals).
    // Fall back to clientId for funeral clients looked up by ID.
    if (recipientClient.phone_number) {
      body.phoneNumber = recipientClient.phone_number;
    } else if (recipientClient.id) {
      body.clientId = recipientClient.id;
    }
    // Lets the backend refuse a text to the person who died (T-210). The
    // phone number above still decides where it goes.
    if (recipientClient.id) body.clientId = recipientClient.id;
    if (caseFuneralId != null) body.funeralId = caseFuneralId;

    const response = await fetch(`${baseUrl}/aircall/messages`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || `Failed to send (${response.status})`);
    }

    // Capture the Aircall message id so the bubble can subscribe to delivery
    // status updates (the message.status_updated webhook fires ~1s later).
    const result = await response.json().catch(() => ({}));
    const aircallMessageId = result?.id ? String(result.id) : undefined;

    // Optimistic event for SMS (webhook will provide the real one)
    return {
      id: `optimistic-${Date.now()}`,
      funeral_id: resolvedFuneralId || 0,
      timestamp: new Date().toISOString(),
      type: CustomerEventType.TEXT_MESSAGE,
      recipient_type: 'customer' as const,
      recipient_id: recipientClient.id,
      recipient_name: recipientClient.name,
      contact_method: 'sms',
      message: sentText,
      user_name: identity?.fullName || undefined,
      aircall_message_id: aircallMessageId,
      metadata: aircallMessageId
        ? { delivery_status: 'pending', sent_at: new Date().toISOString() }
        : undefined,
    };
  };

  const handleSendNote = () =>
    createNote({
      dataProvider,
      message: message.trim(),
      leadId,
      funeralId: resolvedFuneralId,
      userName: identity?.fullName || undefined,
    });

  const doSend = async () => {
    setConfirmRecipient(false);
    setSending(true);
    try {
      const event = activeChannel === 'sms' ? await handleSendSms() : await handleSendNote();
      setMessage('');
      if (onMessageSent && event) {
        onMessageSent(event);
      }
    } catch (err: any) {
      console.error(`Failed to send ${activeChannel}:`, err);
      alert(`Failed to send: ${err?.message || 'Unknown error'}`);
    } finally {
      setSending(false);
    }
  };

  const handleSend = () => {
    if (!canSend) return;
    // Last message came from a different contact than the selected recipient —
    // confirm before sending so a reply doesn't go to the wrong person.
    if (recipientMismatch) {
      setConfirmRecipient(true);
      return;
    }
    void doSend();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && canSend) {
      e.preventDefault();
      handleSend();
    }
  };

  const isInputEnabled = activeChannel === 'sms' || activeChannel === 'note';

  const emailPartner = partners.find((p) => p.partnerId === emailPartnerId) ?? null;
  const emailCaseRecipient: CaseEmailRecipient | undefined = emailPartner
    ? { kind: 'partner', partnerId: emailPartner.partnerId, name: emailPartner.name, email: emailPartner.email || '' }
    : caseFuneralId != null && recipientClient
      ? { kind: 'client', clientId: recipientClient.id }
      : undefined;

  const openEmail = (partnerId: string | null = null) => {
    if (!partnerId && !recipientClient) {
      setEmailHint('Pick who to email first');
      return;
    }
    setEmailHint(null);
    setEmailPartnerId(partnerId);
    setEmailOpen(true);
  };

  const closeEmail = () => {
    setEmailOpen(false);
    setEmailPartnerId(null);
  };

  const handleEmailSent = (sent?: { subject: string; html: string }) => {
    setMessage('');
    if (!onMessageSent || !sent) return;
    const target = emailPartner
      ? { recipient_type: emailPartner.role, recipient_name: emailPartner.name, contact_detail: emailPartner.email || undefined }
      : { recipient_type: 'customer' as const, recipient_id: recipientClient?.id, recipient_name: recipientClient?.name, contact_detail: recipientClient?.email || undefined };
    onMessageSent({
      id: `optimistic-email-${Date.now()}`,
      funeral_id: caseFuneralId || 0,
      timestamp: new Date().toISOString(),
      type: CustomerEventType.EMAIL,
      contact_method: 'email',
      subject: sent.subject,
      message: sent.html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim(),
      user_name: identity?.fullName || undefined,
      direction: 'outbound',
      ...target,
    } as CustomerEvent);
  };

  return (
    <div className="border-t bg-background p-2 sm:p-3 space-y-2 shrink-0 overflow-hidden">
      {/* Wrong-recipient warning */}
      {confirmRecipient && lastInboundClient && recipientClient && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setConfirmRecipient(false)}
        >
          <div
            className="w-full max-w-sm rounded-lg border bg-background p-4 shadow-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 shrink-0 text-amber-500" />
              <div className="space-y-1">
                <h3 className="text-sm font-semibold">Check the recipient</h3>
                <p className="text-sm text-muted-foreground">
                  The last message in this conversation came from{' '}
                  <span className="font-medium" style={{ color: lastInboundClient.color }}>
                    {lastInboundClient.name}
                  </span>
                  , but you&apos;re about to text{' '}
                  <span className="font-medium" style={{ color: recipientClient.color }}>
                    {recipientClient.name}
                  </span>
                  . Send anyway?
                </p>
              </div>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <Button size="sm" variant="outline" onClick={() => setConfirmRecipient(false)}>
                Cancel
              </Button>
              <Button size="sm" onClick={() => void doSend()} className="gap-1.5">
                <Send className="h-3.5 w-3.5" />
                Send to {recipientClient.name}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Channel tabs */}
      <div className="flex items-center gap-1 flex-wrap">
        {channels.map((ch) => {
          const Icon = ch.icon;
          return (
            <button
              key={ch.id}
              onClick={() => (ch.id === 'email' ? openEmail() : setActiveChannel(ch.id))}
              className={cn(
                'flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors',
                activeChannel === ch.id
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-muted',
              )}
            >
              <Icon className="h-3.5 w-3.5" />
              {ch.label}
            </button>
          );
        })}

        {/* Recipient picker: family first, then partners after a '|'. One
            scrolling line on phones; wraps from sm up. */}
        {showRecipientPicker && (
          <div className="flex min-w-0 basis-full items-center gap-1 overflow-x-auto whitespace-nowrap snap-x sm:basis-auto sm:flex-1 sm:flex-wrap sm:overflow-visible sm:whitespace-normal">
            <div className="hidden w-px h-4 bg-border mx-1 sm:block" />
            <span className="shrink-0 text-[11px] text-muted-foreground mr-1">To:</span>
            {clients.length >= 2 && clients.map((client) => {
              const isSelected = client.id === selectedRecipientId && !emailPartnerId;
              return (
                <button
                  key={client.id}
                  onClick={() => setSelectedRecipientId(client.id)}
                  className="shrink-0 snap-start rounded-full px-2.5 py-1 text-[11px] font-medium transition-colors border"
                  style={
                    isSelected
                      ? { backgroundColor: client.color, borderColor: client.color, color: '#fff' }
                      : { backgroundColor: 'transparent', borderColor: client.color, color: client.color }
                  }
                >
                  {client.name}
                </button>
              );
            })}
            {clients.length === 1 && (
              <span
                className="shrink-0 rounded-full px-2.5 py-1 text-[11px] font-medium border"
                style={{ borderColor: clients[0].color, color: clients[0].color }}
              >
                {clients[0].name}
              </span>
            )}
            {showPartners && partners.length > 0 && caseFuneralId != null && (
              <>
                <span className="shrink-0 px-1 text-[11px] text-muted-foreground" aria-hidden>|</span>
                {partners.map((partner) => (
                  <PartnerRecipientChip
                    key={`${partner.role}-${partner.partnerId}`}
                    partner={partner}
                    funeralId={caseFuneralId}
                    selected={emailPartnerId === partner.partnerId}
                    onSelect={() => openEmail(partner.partnerId)}
                  />
                ))}
              </>
            )}
          </div>
        )}
      </div>

      {/* Input area */}
      <div className="relative">
        <textarea
          ref={textareaRef}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={
            activeChannel !== 'note' && recipientClient && (showRecipientPicker || clients.length === 1)
              ? `${active.placeholder.replace('...', '')} to ${recipientClient.name}...`
              : active.placeholder
          }
          disabled={!isInputEnabled || sending}
          rows={2}
          className="block w-full resize-none overflow-y-auto field-sizing-content rounded-lg border bg-muted/30 px-3 py-2 text-sm leading-5 placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-60"
          style={{
            minHeight: TEXTAREA_MIN_HEIGHT,
            maxHeight: TEXTAREA_MAX_HEIGHT,
            ...(recipientClient && showRecipientPicker
              ? { borderLeftColor: recipientClient.color, borderLeftWidth: 3, borderLeftStyle: 'solid' as const }
              : {}),
          }}
        />
        <div className="flex items-center justify-between mt-1.5">
          <div className="flex items-center gap-1 text-[11px] text-muted-foreground">
            {emailHint && <span>{emailHint}</span>}
          </div>
          <div className="flex items-center gap-1.5">
            {activeChannel !== 'note' && hasEntityId && (
              <Button
                size="sm"
                variant="outline"
                disabled={sending}
                onClick={() => setNoteDialogOpen(true)}
                className="gap-1.5"
                title="Add an internal note (never sends an SMS)"
              >
                <StickyNote className="h-3.5 w-3.5" />
                Note
              </Button>
            )}
            {activeChannel === 'sms' && (
              <Button
                size="sm"
                variant="outline"
                disabled={!message.trim() || translating || sending}
                onClick={handleTranslateToFrench}
                className="gap-1.5"
                title="Translate to French"
              >
                {translating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wand2 className="h-3.5 w-3.5" />}
                FR
              </Button>
            )}
            <Button size="sm" disabled={!canSend} onClick={handleSend} className="gap-1.5">
              {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
              {activeChannel === 'note' ? 'Add note' : 'Send'}
            </Button>
          </div>
        </div>
      </div>

      {/* Email: the contact card's dialog, addressed from the selected chip. */}
      <EmailComposeDialog
        open={emailOpen}
        onClose={closeEmail}
        recipient={
          emailPartner
            ? { name: emailPartner.name, email: emailPartner.email }
            : { name: recipientClient?.name, email: recipientClient?.email }
        }
        context={
          caseFuneralId != null
            ? { funeralId: caseFuneralId, ...(emailPartner || !recipientClient ? {} : { clientId: recipientClient.id }) }
            : { ...(leadId ? { leadId } : {}) }
        }
        caseRecipient={emailCaseRecipient}
        initialText={message}
        onSent={handleEmailSent}
      />

      {/* Standalone note composer — an alternative to switching the channel tab */}
      <AddNoteDialog
        open={noteDialogOpen}
        onOpenChange={setNoteDialogOpen}
        leadId={leadId}
        funeralId={resolvedFuneralId}
        onNoteAdded={onMessageSent}
      />
    </div>
  );
}
